import { useEffect, useState, useRef } from "react";
import { ChatView } from "@/components/ChatView";
import { ConversationList } from "@/components/ConversationList";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import type { LogEntry } from "@/types";
import { Menu, Eye, EyeOff } from "lucide-react";
import "./index.css";

function LoginForm({ onLogin }: { onLogin: (token: string) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        const { token } = await res.json();
        onLogin(token);
      } else {
        setError("IDまたはパスワードが正しくありません");
      }
    } catch {
      setError("サーバーに接続できませんでした");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 px-4">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-bold">お雛祭りAIログ</h1>
          <p className="text-sm text-muted-foreground">ログインしてください</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="username">ID</Label>
            <Input
              id="username"
              ref={usernameRef}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="password">パスワード</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
                aria-label={showPassword ? "パスワードを隠す" : "パスワードを表示する"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "認証中..." : "ログイン"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export function App() {
  const [requireAuth, setRequireAuth] = useState<boolean | null>(null);
  const [token, setToken] = useLocalStorage<string | null>("logviewer-auth-token", null);
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [selectedIndex, setSelectedIndex] = useLocalStorage<number | null>("logviewer-selected-index", null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetch("/api/config")
      .then((res) => res.json())
      .then((data) => setRequireAuth(data.requireAuth ?? true))
      .catch(() => setRequireAuth(true));
  }, []);

  const handleLogin = (newToken: string) => {
    setToken(newToken);
  };

  const handleLogout = async () => {
    if (token) {
      await fetch("/api/auth", {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    setToken(null);
    setEntries([]);
  };

  useEffect(() => {
    if (requireAuth === null) return;
    if (requireAuth && !token) return;
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    fetch("/api/logs", { headers })
      .then(async (res) => {
        if (res.status === 401) {
          setToken(null);
          return [];
        }
        return res.json() as Promise<LogEntry[]>;
      })
      .then((data) => setEntries(data ?? []))
      .catch(() => setEntries([]));
  }, [token, requireAuth]);

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
    setSidebarOpen(false);
  };

  const selectedEntry = selectedIndex !== null && entries[selectedIndex] ? entries[selectedIndex] : null;

  const conversationList = (
    <ConversationList
      entries={entries}
      selectedIndex={selectedIndex}
      onSelect={handleSelect}
    />
  );

  if (requireAuth === null) {
    return null;
  }

  if (requireAuth && !token) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <header className="flex items-center gap-2 border-b px-4 py-2 shrink-0">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <SheetHeader className="px-4 py-3 border-b">
              <SheetTitle>会話一覧</SheetTitle>
            </SheetHeader>
            {conversationList}
          </SheetContent>
        </Sheet>
        <h1 className="text-xl font-bold">お雛祭りAIログ</h1>
        {requireAuth && (
          <div className="ml-auto">
            <Button variant="ghost" size="sm" onClick={handleLogout}>ログアウト</Button>
          </div>
        )}
      </header>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex md:flex-col w-72 border-r shrink-0">
          <div className="px-4 py-3 border-b font-semibold text-sm">会話一覧</div>
          <div className="flex-1 overflow-hidden">
            {conversationList}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4">
          <ChatView entry={selectedEntry} />
        </main>
      </div>
    </div>
  );
}

export default App;
